import React from 'react';
import { BookOpen, TrendingUp, Users, Heart, Briefcase, Lock, ShoppingBag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { PageLayout } from '../components/PageLayout';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { SEO } from '../components/SEO';
export function EducationPage() {
  const {
    language,
    t
  } = useLanguage();
  const scamPatterns = [{
    icon: Users,
    title: t('scam.socialEngineering'),
    description: t('education.scam.socialEngineering.desc'),
    tactics: [t('tactic.impersonation'), t('tactic.authorityExploitation'), t('tactic.urgencyCreation'), t('tactic.fearTactics')],
    color: 'blue',
    protocolId: 'social-engineering'
  }, {
    icon: TrendingUp,
    title: t('scam.investmentFraud'),
    description: t('education.scam.investmentFraud.desc'),
    tactics: [t('tactic.guaranteedProfits'), t('tactic.ponziSchemes'), t('tactic.pumpAndDump'), t('tactic.fakeTradingPlatforms')],
    color: 'green',
    protocolId: 'investment-crypto'
  }, {
    icon: Heart,
    title: t('scam.romanceScam'),
    description: t('education.scam.romanceScam.desc'),
    tactics: [t('tactic.fakeProfiles'), t('tactic.loveBombing'), t('tactic.sobStories'), t('tactic.moneyRequests')],
    color: 'pink',
    protocolId: 'romance-scam'
  }, {
    icon: Lock,
    title: t('scam.phishing'),
    description: t('education.scam.phishing.desc'),
    tactics: [t('tactic.fakeEmails'), t('tactic.spoofedWebsites'), t('tactic.smsPhishing'), t('tactic.voicePhishing')],
    color: 'red',
    protocolId: 'phishing-account-takeover'
  }, {
    icon: ShoppingBag,
    title: t('scam.ecommerce'),
    description: t('education.scam.ecommerce.desc'),
    tactics: [t('tactic.fakeSellers'), t('tactic.nonDelivery'), t('tactic.counterfeitGoods'), t('tactic.paymentFraud')],
    color: 'purple',
    protocolId: 'marketplace-payment'
  }, {
    icon: Briefcase,
    title: t('scam.jobScam'),
    description: t('education.scam.jobScam.desc'),
    tactics: [t('tactic.upfrontFees'), t('tactic.workFromHomeSchemes'), t('tactic.pyramidSchemes'), t('tactic.fakeRecruiters')],
    color: 'orange',
    protocolId: 'job-scam'
  }];
  const redFlags = [t('redFlag.immediateAction'), t('redFlag.tooGood'), t('redFlag.personalInfo'), t('redFlag.secrecy'), t('redFlag.unusualPayment'), t('redFlag.poorGrammar'), t('redFlag.unsolicited'), t('redFlag.threats')];
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    pink: 'bg-pink-50 text-pink-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };
  return <>
      <SEO title={t('seo.education.title')} description={t('seo.education.desc')} data-id="element-646" />
      <PageLayout title={t('education.title')} subtitle={t('education.subtitle')} icon={<BookOpen className="h-8 w-8 text-primary-600" data-id="element-648" />} maxWidth="xl" data-id="element-647">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-649">
          {scamPatterns.map((pattern, index) => {
          const Icon = pattern.icon;
          return <Link key={index} to={`/${language}/protocols/${pattern.protocolId}`} className="block group" data-id="element-650">
                <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 animate-fade-in cursor-pointer" style={{
              animationDelay: `${index * 50}ms`
            }} data-id="element-651">
                  <div className="flex items-start gap-4 mb-4" data-id="element-652">
                    <div className={`flex-shrink-0 p-3 rounded-xl ${colorClasses[pattern.color]} group-hover:scale-110 transition-transform`} data-id="element-653">
                      <Icon className="h-6 w-6" data-id="element-654" />
                    </div>
                    <div className="flex-1" data-id="element-655">
                      <h3 className="text-xl font-bold mb-2 text-primary-900 group-hover:text-accent-600 transition-colors" data-id="element-656">
                        {pattern.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-657">
                        {pattern.description}
                      </p>
                    </div>
                  </div>
                  <div className="ml-16" data-id="element-658">
                    <h4 className="font-semibold text-sm text-slate-700 mb-2" data-id="element-659">
                      {t('education.commonTactics')}
                    </h4>
                    <ul className="space-y-1.5 mb-4" data-id="element-660">
                      {pattern.tactics.map((tactic, i) => <li key={i} className="text-sm text-slate-600 flex items-center gap-2" data-id="element-661">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" data-id="element-662" />
                          {tactic}
                        </li>)}
                    </ul>
                    <div className="flex items-center text-sm font-medium text-accent-600 group-hover:text-accent-700" data-id="element-663">
                      {t('education.viewProtocol')}
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-664" />
                    </div>
                  </div>
                </Card>
              </Link>;
        })}
        </div>

        {/* Red Flags Section */}
        <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 mb-12" data-id="element-665">
          <h2 className="text-2xl font-bold mb-6 text-red-900" data-id="element-666">
            {t('education.redFlags')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-667">
            {redFlags.map((flag, index) => <div key={index} className="flex items-start gap-3 p-4 bg-white/80 rounded-lg border border-red-100" data-id="element-668">
                <span className="text-red-600 font-bold text-lg flex-shrink-0" data-id="element-669">
                  ⚠
                </span>
                <span className="text-slate-800 text-sm font-medium" data-id="element-670">
                  {flag}
                </span>
              </div>)}
          </div>
        </Card>

        <div className="text-center" data-id="element-671">
          <Button href={`/${language}/check-scam`} variant="accent" size="lg" data-id="element-672">
            {t('education.checkScamBtn')}
          </Button>
        </div>
      </PageLayout>
    </>;
}